import { isDevelopmentEnv, isStagingEnv } from '@mc/client-env'
import { logger } from '@mc/next-logger'

/**
 * Safely retrieves a value from localStorage.
 * Returns null if localStorage is unavailable or an error occurs.
 * Errors are only logged in development and staging environments.
 *
 * @param key - The key to retrieve from localStorage.
 * @returns The value corresponding to the key, or null if an error occurs.
 */
export const safeGetLocalStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined')
    return null

  try {
    return localStorage.getItem(key)
  } catch (err) {
    if (isDevelopmentEnv() || isStagingEnv()) {
      logger.error(`Error reading localStorage key "${key}":`, err)
    }
    return null
  }
}

/**
 * The same as `safeGetLocalStorageItem` but for multiple keys.
 *
 * @param keys - An array of localStorage keys to retrieve.
 * @returns An array of values corresponding to each key, or null for each key if localStorage is unavailable or an error occurs.
 */
export const safeGetLocalStorageItems = (
  keys: string[],
): Array<string | null> => keys.map((key) => safeGetLocalStorageItem(key))
